import React, { useEffect, useState } from 'react';

import { Form } from 'antd';
import { uuid } from 'short-uuid';
import styled from 'styled-components';

import { FeeType } from 'types/fees';

import ShopDataModalFeesCommunityFees from './ShopDataModalFeesCommunityFees';
import ShopDataModalFeesContractSelection from './ShopDataModalFeesContractSelection';
import ShopDataModalLevelFees from './ShopDataModalLevelFees';
import { useContractContext } from '../../../common/Contract/ContractContext';
import {
  DEFAULT_FEES_FIELDS,
  FEES_FIELD_COMMUNITY_FEE,
  FEES_FIELD_COMMUNITY_FEE_EQUIVALENT_ENABLED,
  FEES_FIELD_FREQUENCY,
  FEES_FIELD_FREQUENCY_PERCENTAGE,
  FEES_LEVELS_FIELDS,
  FIELD_CATEGORY,
  LEVEL_FEES_FIELDS,
} from '../constants';
import ShopDataModalAddContracts from '../contracts/ShopDataModalAddContracts';
import { useShopDataModalContext } from '../ShopDataModalContext';

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const ShopDataModalFees = () => {
  const [key, setKey] = useState(uuid());
  const {
    form,
    currentLivingType,
    currentData,
    community,
    isCommunityCcrc,
    disabled,
    setCurrentData,
    handleDataStatusChanged,
  } = useShopDataModalContext();
  const { contracts, selectedKey, handleAvailableContracts } = useContractContext();

  useEffect(() => {
    const livingTypeKey = String(currentLivingType?.abv);
    const currentFeesLivingTypeData = currentData[FIELD_CATEGORY.FEES_INCENTIVES][livingTypeKey] ?? {};
    const currentCareFeesLivingTypeData = currentData[FIELD_CATEGORY.FEES][livingTypeKey] ?? {};
    const availableContractKeys = [
      ...Object.keys(currentFeesLivingTypeData),
      ...Object.keys(currentCareFeesLivingTypeData),
    ];

    if (availableContractKeys) {
      handleAvailableContracts(Array.from(new Set(availableContractKeys)), selectedKey);
    }

    if (selectedKey) {
      const currentFeesLivingTypeContractData = currentFeesLivingTypeData[selectedKey];
      const currentCareFeesLivingTypeContractData = currentCareFeesLivingTypeData[selectedKey];

      form?.setFieldValue([FIELD_CATEGORY.FEES_INCENTIVES], currentFeesLivingTypeContractData);
      form?.setFieldValue([FIELD_CATEGORY.FEES], currentCareFeesLivingTypeContractData);
    }
  }, [key, currentLivingType, selectedKey, community]);

  const handleChange = () => setKey(uuid());

  const handleValuesChange = (values: any, allValues: any) => {
    if (!selectedKey) return;

    const livingTypeKey = String(currentLivingType?.abv);

    handleDataStatusChanged([FIELD_CATEGORY.FEES_INCENTIVES, FIELD_CATEGORY.FEES]);

    const feesValues = values?.[FIELD_CATEGORY.FEES] ?? {};

    const feeTypeKeysChanged = Object.entries(feesValues).reduce<string[]>((acc, [feeTypeKey, feeValue]) => {
      if (feeValue) {
        const feeValueRecord = feeValue as Record<string, unknown>;
        if (LEVEL_FEES_FIELDS.FEES_PRICING_TYPE in feeValueRecord) {
          acc.push(feeTypeKey);
        }
      }
      return acc;
    }, []);

    const feeEquivalentStatusChanged =
      FEES_FIELD_COMMUNITY_FEE_EQUIVALENT_ENABLED in (values?.[FIELD_CATEGORY.FEES_INCENTIVES] || {});

    let feesFieldsOverrides = {};
    let careFeesFieldsOverrides: Record<string, any> = {};

    if (feeEquivalentStatusChanged) {
      const resetFields = values?.[FIELD_CATEGORY.FEES_INCENTIVES]?.[FEES_FIELD_COMMUNITY_FEE_EQUIVALENT_ENABLED]
        ? [FEES_FIELD_COMMUNITY_FEE]
        : [FEES_FIELD_FREQUENCY, FEES_FIELD_FREQUENCY_PERCENTAGE];

      const feesDefaults = resetFields.reduce((acc: any, key: any) => {
        acc[key] = null;
        return acc;
      }, {});

      feesFieldsOverrides = { ...feesDefaults };
    }

    if (feeTypeKeysChanged.length) {
      const careFeesLevelsDefaults = Object.values(FEES_LEVELS_FIELDS).reduce((acc: any, key: any) => {
        acc[key] = null;
        return acc;
      }, {});

      careFeesFieldsOverrides = feeTypeKeysChanged.reduce<Record<string, any>>((acc, feeTypeKey) => {
        acc[feeTypeKey] = {
          [LEVEL_FEES_FIELDS.FEE_NOTE]: '',
          [LEVEL_FEES_FIELDS.AVERAGE_COST]: null,
          ...careFeesLevelsDefaults,
        };
        return acc;
      }, {});
    }

    const feesIncentivesAllValues = allValues?.[FIELD_CATEGORY.FEES_INCENTIVES] ?? {};
    const careFeesAllValues = allValues?.[FIELD_CATEGORY.FEES] ?? {};

    const addDefaultFieldToFieldsArray = (field: string) => {
      const prevSelected =
        currentData[FIELD_CATEGORY.FEES_INCENTIVES]?.[livingTypeKey]?.[selectedKey] || {};
      const prevFields = prevSelected.fields || [];
      const newFields = prevFields.includes(field) ? prevFields : [...prevFields, field];

      setCurrentData((prevState: any) => ({
        ...prevState,
        [FIELD_CATEGORY.FEES_INCENTIVES]: {
          ...prevState[FIELD_CATEGORY.FEES_INCENTIVES],
          [livingTypeKey]: {
            ...prevState[FIELD_CATEGORY.FEES_INCENTIVES][livingTypeKey],
            [selectedKey]: {
              ...prevSelected,
              fields: newFields,
            },
          },
        },
      }));
    };

    DEFAULT_FEES_FIELDS.forEach((field) => {
      if (feesIncentivesAllValues[field] != null) {
        addDefaultFieldToFieldsArray(field);
      }
    });

    setCurrentData((prevState: any) => {
      const prevFeesIncentivesCategory = prevState[FIELD_CATEGORY.FEES_INCENTIVES] ?? {};
      const prevFeesIncentivesLivingType = prevFeesIncentivesCategory[livingTypeKey] ?? {};
      const prevFeesCategory = prevState[FIELD_CATEGORY.FEES] ?? {};
      const prevLivingTypeFees = prevFeesCategory[livingTypeKey] ?? {};
      const prevContractFees = prevLivingTypeFees[selectedKey] ?? {};

      const nextContractFees = { ...prevContractFees };
      const feeTypeKeys = new Set<string>([
        ...Object.keys(prevContractFees || {}),
        ...Object.keys(careFeesAllValues || {}),
        ...Object.keys(careFeesFieldsOverrides),
      ]);

      feeTypeKeys.forEach((feeTypeKey) => {
        const feeOverrides = careFeesFieldsOverrides[feeTypeKey];
        const formValue = careFeesAllValues?.[feeTypeKey];
        if (!formValue && !feeOverrides) return;

        nextContractFees[feeTypeKey] = {
          fields: [],
          fee_type: Number(feeTypeKey),
          ...prevContractFees?.[feeTypeKey],
          ...(formValue || {}),
          ...(feeOverrides || {}),
          living_type_id: currentLivingType?.id,
        };
      });

      return {
        ...prevState,
        [FIELD_CATEGORY.FEES_INCENTIVES]: {
          ...prevFeesIncentivesCategory,
          [livingTypeKey]: {
            ...prevFeesIncentivesLivingType,
            [selectedKey]: {
              fields: prevFeesIncentivesLivingType?.[selectedKey]?.fields || [],
              ...prevFeesIncentivesLivingType?.[selectedKey],
              ...feesIncentivesAllValues,
              ...feesFieldsOverrides,
              living_type_id: currentLivingType?.id,
            },
          },
        },
        [FIELD_CATEGORY.FEES]: {
          ...prevFeesCategory,
          [livingTypeKey]: {
            ...prevLivingTypeFees,
            [selectedKey]: nextContractFees,
          },
        },
      };
    });

    if (feeTypeKeysChanged.length || feeEquivalentStatusChanged) {
      handleChange();
    }
  };

  const hasContracts = Boolean(contracts.length);

  return (
    <Container key={key}>
      <ShopDataModalFeesContractSelection />
      <Form form={form} disabled={disabled} onValuesChange={handleValuesChange}>
        {isCommunityCcrc && !hasContracts && (
          <ShopDataModalAddContracts categories={[FIELD_CATEGORY.FEES_INCENTIVES, FIELD_CATEGORY.FEES]} />
        )}
        {(!isCommunityCcrc || hasContracts) && (
          <>
            <ShopDataModalFeesCommunityFees onChange={handleChange} />
            <ShopDataModalLevelFees type={FeeType.CARE_FEE} onChange={handleChange} />
            <ShopDataModalLevelFees type={FeeType.MEDICATION_FEE} onChange={handleChange} />
          </>
        )}
      </Form>
    </Container>
  );
};

export default ShopDataModalFees;
